import { useCallback, useMemo, useState } from 'react'
import useIsomorphicLayoutEffect from 'use-isomorphic-layout-effect'
import { resolveGridGeometry, type GridGeometry } from '../utils/gridGeometry'
import {
  createGridSnap,
  GRID_TILE_SIZE,
  type GridSnapHelpers,
} from '../utils/gridSnap'

export interface UseGridGeometryOptions {
  cols?: number
  rows?: number
  minCols?: number
  tileSize?: number
  fixed?: boolean
}

export interface UseGridGeometryResult extends GridSnapHelpers {
  ref: (node: HTMLElement | null) => void
  geometry: GridGeometry
  /**
   * Whether `geometry` is settled: a real container width has been observed,
   * or `fixed` pinned it so none is ever needed. `geometry.isDesignWidth`
   * cannot answer this — it is also true before the first measurement, when
   * the resolver is still returning the design board as its fallback.
   */
  measured: boolean
}

export function useGridGeometry({
  cols = 30,
  rows = 16,
  minCols = 1,
  tileSize = GRID_TILE_SIZE,
  fixed = false,
}: UseGridGeometryOptions = {}): UseGridGeometryResult {
  const [node, setNode] = useState<HTMLElement | null>(null)
  const [availableWidth, setAvailableWidth] = useState(Number.NaN)

  const ref = useCallback((next: HTMLElement | null) => setNode(next), [])

  useIsomorphicLayoutEffect(() => {
    if (fixed || !node) return undefined

    setAvailableWidth(node.getBoundingClientRect().width)

    if (typeof ResizeObserver === 'undefined') return undefined
    const observer = new ResizeObserver(([entry]) => {
      if (entry) setAvailableWidth(entry.contentRect.width)
    })
    observer.observe(node)
    return () => observer.disconnect()
  }, [fixed, node])

  const geometry = useMemo(
    () =>
      resolveGridGeometry({
        availableWidth: fixed ? Number.POSITIVE_INFINITY : availableWidth,
        cols,
        rows,
        minCols,
        tileSize,
      }),
    [availableWidth, cols, rows, minCols, tileSize, fixed],
  )

  const helpers = useMemo(
    () => createGridSnap(geometry.tileSize),
    [geometry.tileSize],
  )

  const measured =
    fixed || (Number.isFinite(availableWidth) && availableWidth > 0)

  return useMemo(
    () => ({ ...helpers, ref, geometry, measured }),
    [helpers, ref, geometry, measured],
  )
}
